import type { TaskListItemDto } from '@ovo/shared'
import type { ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { errorMessage } from '../api/client.ts'
import { useMe, useTasks } from '../api/hooks.ts'
import { useNow } from '../lib/hooks.ts'
import { Button } from '../ui/Button.tsx'
import type { FilterOption } from '../ui/Chip.tsx'
import { FilterChips } from '../ui/Chip.tsx'
import { EmptyState, ErrorState } from '../ui/EmptyState.tsx'
import { Section } from '../ui/List.tsx'
import { Screen, ScreenHeader } from '../ui/ScreenHeader.tsx'
import { SkeletonList } from '../ui/Skeleton.tsx'
import { useTaskActions } from '../ui/TaskActions.tsx'
import { TaskCell } from '../ui/TaskCell.tsx'

type Filter = 'review' | 'accept' | 'overdue' | 'requests' | 'active' | 'archive'

const FILTERS: Filter[] = ['review', 'accept', 'overdue', 'requests', 'active', 'archive']

const EMPTY: Record<Filter, { title: string; text: string }> = {
  review: { title: 'Проверять нечего', text: 'Когда исполнитель сдаст работу, она появится здесь.' },
  accept: { title: 'Все задачи приняты', text: 'Новые задачи, которые ещё не приняли, будут здесь.' },
  overdue: { title: 'Сроки в порядке', text: 'Задач, у которых прошёл срок, нет.' },
  requests: {
    title: 'Вопросов нет',
    text: 'Вопросы исполнителей и просьбы о переносе срока появятся здесь.',
  },
  active: { title: 'Вы пока не ставили задач', text: 'Нажмите «+», чтобы поставить задачу коллеге.' },
  archive: { title: 'Архив пуст', text: 'Здесь будут выполненные и отменённые задачи.' },
}

function matches(t: TaskListItemDto, f: Filter): boolean {
  switch (f) {
    case 'review':
      return t.status === 'submitted'
    case 'accept':
      return t.status === 'new'
    case 'overdue':
      return t.isOverdue
    case 'requests':
      return t.pendingRequest !== null
    case 'active':
    case 'archive':
      return true
  }
}

export function OutboxScreen() {
  const me = useMe()
  const meId = me.data?.id ?? 0
  const [params, setParams] = useSearchParams()
  const fromUrl = params.get('f') as Filter | null
  const [picked, setPicked] = useState<Filter | null>(fromUrl && FILTERS.includes(fromUrl) ? fromUrl : null)
  const open = useTasks({ scope: 'created' }, Boolean(me.data))
  const now = useNow()
  const actions = useTaskActions(meId)

  // задачи себе показываем в «Мои», здесь — только поставленные другим
  const list = useMemo(() => (open.data ?? []).filter((t) => t.assignee.id !== meId), [open.data, meId])
  const counts = useMemo(() => {
    const c = {} as Record<Filter, number>
    for (const f of FILTERS) c[f] = f === 'archive' ? 0 : list.filter((t) => matches(t, f)).length
    return c
  }, [list])

  const filter: Filter = picked ?? (counts.review > 0 ? 'review' : 'active')
  const closed = useTasks({ scope: 'created', state: 'closed' }, Boolean(me.data) && filter === 'archive')

  const setFilter = (f: Filter): void => {
    setPicked(f)
    setParams(f === 'active' ? {} : { f }, { replace: true })
  }

  const options: FilterOption<Filter>[] = [
    { value: 'review', label: 'На проверке', count: counts.review },
    { value: 'accept', label: 'Ждут принятия', count: counts.accept },
    { value: 'overdue', label: 'Срок прошёл', count: counts.overdue, warn: counts.overdue > 0 },
    { value: 'requests', label: 'Вопросы и переносы', count: counts.requests, warn: counts.requests > 0 },
    { value: 'active', label: 'Активные', count: counts.active },
    { value: 'archive', label: 'Архив' },
  ]

  const source = filter === 'archive' ? (closed.data ?? []).filter((t) => t.assignee.id !== meId) : list
  const visible = source.filter((t) => matches(t, filter))
  const loading = open.isPending || me.isPending || (filter === 'archive' && closed.isPending)
  const error = open.error ?? me.error ?? (filter === 'archive' ? closed.error : null)

  let body: ReactNode
  if (loading) body = <SkeletonList rows={5} />
  else if (error) body = <ErrorState text={errorMessage(error)} onRetry={() => void open.refetch()} />
  else if (visible.length === 0) body = <EmptyState title={EMPTY[filter].title} text={EMPTY[filter].text} />
  else {
    body = (
      <Section
        id="outbox"
        title={options.find((o) => o.value === filter)?.label}
        count={visible.length}
        footer={
          filter === 'requests' ? 'Ответьте на вопрос или решите по сроку — исполнитель ждёт.' : undefined
        }
      >
        {visible.map((t) => (
          <TaskCell
            key={t.id}
            task={t}
            meId={meId}
            lead="assignee"
            now={now}
            onGlyph={filter === 'archive' ? undefined : (task) => actions.openMenu(task)}
            action={
              t.status === 'submitted' ? (
                <Button
                  variant="primary"
                  size="sm"
                  loading={actions.pendingTaskId === t.id}
                  onClick={() => actions.run(t, 'verify')}
                  aria-label={`Принять работу по задаче «${t.title}»`}
                >
                  Принять
                </Button>
              ) : undefined
            }
          />
        ))}
      </Section>
    )
  }

  return (
    <Screen root label="Поставленные задачи">
      <ScreenHeader title="Поставил" />
      <p className="screen-subtitle">Задачи, которые вы поставили коллегам</p>
      <FilterChips label="Фильтр поставленных задач" options={options} value={filter} onChange={setFilter} />
      {body}
      {actions.element}
    </Screen>
  )
}
